import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import dashboardService, {
  GeneralStats,
  FleetDistributionItem,
  MemberDistribution,
  MileageStats as MileageStatsData,
  MaintenanceAndFuelStats as MaintenanceAndFuelStatsType,
  RecentActivity,
  FleetStatus
} from '../../services/dashboardService';
import {
  Container,
  Grid,
  Typography,
  Button,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Skeleton,
  Tooltip,
  IconButton,
  Snackbar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import StatCard from './components/StatCard';
import FleetDistributionChart from './components/FleetDistributionChart';
import MemberDistributionStats from './components/MemberDistributionStats';
import MileageStats from './components/MileageStats';
import MaintenanceAndFuelStats from './components/MaintenanceAndFuelStats';
import RecentActivityList from './components/RecentActivityList';
import FleetStatusBars from './components/FleetStatusBars';

/**
 * Interface pour les données du tableau de bord
 */
interface DashboardData {
  generalStats: GeneralStats;
  fleetDistribution: FleetDistributionItem[];
  memberDistribution: MemberDistribution;
  mileageStats: MileageStatsData;
  maintenanceAndFuelStats: MaintenanceAndFuelStatsType;
  recentActivities: RecentActivity[];
  fleetStatus: FleetStatus;
}

/**
 * Interface pour l'état de chargement de chaque section
 */
interface LoadingState {
  generalStats: boolean;
  fleetDistribution: boolean;
  memberDistribution: boolean;
  mileageStats: boolean;
  maintenanceAndFuelStats: boolean;
  recentActivities: boolean;
  fleetStatus: boolean;
}

/**
 * Interface pour l'état d'erreur de chaque section
 */
interface ErrorState {
  generalStats: string | null;
  fleetDistribution: string | null;
  memberDistribution: string | null;
  mileageStats: string | null;
  maintenanceAndFuelStats: string | null;
  recentActivities: string | null;
  fleetStatus: string | null;
}

/**
 * Page principale du tableau de bord
 */
const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState<LoadingState>({
    generalStats: true,
    fleetDistribution: true,
    memberDistribution: true,
    mileageStats: true,
    maintenanceAndFuelStats: true,
    recentActivities: true,
    fleetStatus: true
  });
  const [error, setError] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<ErrorState>({
    generalStats: null,
    fleetDistribution: null,
    memberDistribution: null,
    mileageStats: null,
    maintenanceAndFuelStats: null,
    recentActivities: null,
    fleetStatus: null
  });
  const [dashboardData, setDashboardData] = useState<Partial<DashboardData>>({});
  const [refreshing, setRefreshing] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const loadDashboardData = async (organizationId: string) => {
    setLoading(true);
    setSectionLoading({
      generalStats: true,
      fleetDistribution: true,
      memberDistribution: true,
      mileageStats: true,
      maintenanceAndFuelStats: true,
      recentActivities: true,
      fleetStatus: true
    });
    setSectionErrors({
      generalStats: null,
      fleetDistribution: null,
      memberDistribution: null,
      mileageStats: null,
      maintenanceAndFuelStats: null,
      recentActivities: null,
      fleetStatus: null
    });
    setError(null);

    // Charger les statistiques générales
    try {
      const generalStats = await dashboardService.getGeneralStats(organizationId);
      setDashboardData(prev => ({ ...prev, generalStats }));
    } catch (err) {
      console.error('Error loading general stats:', err);
      setSectionErrors(prev => ({ ...prev, generalStats: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, generalStats: false }));
    }

    // Charger la répartition par flotte
    try {
      const fleetDistribution = await dashboardService.getFleetDistribution(organizationId);
      setDashboardData(prev => ({ ...prev, fleetDistribution }));
    } catch (err) {
      console.error('Error loading fleet distribution:', err);
      setSectionErrors(prev => ({ ...prev, fleetDistribution: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, fleetDistribution: false }));
    }

    // Charger la répartition par membre
    try {
      const memberDistribution = await dashboardService.getMemberDistribution(organizationId);
      setDashboardData(prev => ({ ...prev, memberDistribution }));
    } catch (err) {
      console.error('Error loading member distribution:', err);
      setSectionErrors(prev => ({ ...prev, memberDistribution: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, memberDistribution: false }));
    }

    // Charger les statistiques kilométriques
    try {
      const mileageStats = await dashboardService.getMileageStats(organizationId);
      setDashboardData(prev => ({ ...prev, mileageStats }));
    } catch (err) {
      console.error('Error loading mileage stats:', err);
      setSectionErrors(prev => ({ ...prev, mileageStats: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, mileageStats: false }));
    }

    // Charger les statistiques d'entretien et carburant
    try {
      const maintenanceAndFuelStats = await dashboardService.getMaintenanceAndFuelStats(organizationId);
      setDashboardData(prev => ({ ...prev, maintenanceAndFuelStats }));
    } catch (err) {
      console.error('Error loading maintenance and fuel stats:', err);
      setSectionErrors(prev => ({ ...prev, maintenanceAndFuelStats: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, maintenanceAndFuelStats: false }));
    }

    // Charger les activités récentes
    try {
      const recentActivities = await dashboardService.getRecentActivities(organizationId);
      setDashboardData(prev => ({ ...prev, recentActivities }));
    } catch (err) {
      console.error('Error loading recent activities:', err);
      setSectionErrors(prev => ({ ...prev, recentActivities: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, recentActivities: false }));
    }

    // Charger l'état de la flotte
    try {
      const fleetStatus = await dashboardService.getFleetStatus(organizationId);
      setDashboardData(prev => ({ ...prev, fleetStatus }));
    } catch (err) {
      console.error('Error loading fleet status:', err);
      setSectionErrors(prev => ({ ...prev, fleetStatus: t('dashboard:errors.loadError') }));
    } finally {
      setSectionLoading(prev => ({ ...prev, fleetStatus: false }));
    }

    setLoading(false);
    
    if (refreshing) {
      setRefreshing(false);
      setSnackbarMessage(t('dashboard:refreshComplete'));
      setSnackbarOpen(true);
    }
  };

  const handleRefresh = () => {
    if (!user?.organizationId) return;
    setRefreshing(true);
    loadDashboardData(user.organizationId);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    if (!user?.organizationId) {
      setError(t('common:organizationRequired'));
      setLoading(false);
      return;
    }

    loadDashboardData(user.organizationId);
  }, [user, t]);

  if (loading && Object.values(sectionLoading).every(v => v)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1">
            {t('dashboard:title')}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t('dashboard:subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('dashboard:refresh')}>
            <IconButton
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ mr: 1 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/vehicles', { state: { openAddVehicleForm: true } })}
          >
            {t('dashboard:addVehicle')}
          </Button>
        </Box>
      </Box>

      {/* Statistiques générales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          {sectionLoading.generalStats ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ) : sectionErrors.generalStats ? (
            <Alert severity="error">{sectionErrors.generalStats}</Alert>
          ) : dashboardData.generalStats ? (
            <StatCard
              icon="vehicle"
              value={dashboardData.generalStats.totalVehicles}
              label={t('dashboard:generalStats.totalVehicles')}
            />
          ) : (
            <Alert severity="warning">{t('dashboard:errors.noData')}</Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {sectionLoading.generalStats ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ) : sectionErrors.generalStats ? (
            <Alert severity="error">{sectionErrors.generalStats}</Alert>
          ) : dashboardData.generalStats ? (
            <StatCard
              icon="member"
              value={dashboardData.generalStats.activeMembers}
              label={t('dashboard:generalStats.activeMembers')}
            />
          ) : (
            <Alert severity="warning">{t('dashboard:errors.noData')}</Alert>
          )}
        </Grid>
        <Grid item xs={12} sm={4}>
          {sectionLoading.generalStats ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ) : sectionErrors.generalStats ? (
            <Alert severity="error">{sectionErrors.generalStats}</Alert>
          ) : dashboardData.generalStats ? (
            <StatCard
              icon="mileage"
              value={dashboardData.generalStats.totalKilometers.toLocaleString()}
              label={t('dashboard:generalStats.totalKilometers')}
            />
          ) : (
            <Alert severity="warning">{t('dashboard:errors.noData')}</Alert>
          )}
        </Grid>
      </Grid>

      {/* Répartition par flotte et par membre */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard:fleetDistribution.title')}
            </Typography>
            {sectionLoading.fleetDistribution ? (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : sectionErrors.fleetDistribution ? (
              <Alert severity="error">{sectionErrors.fleetDistribution}</Alert>
            ) : dashboardData.fleetDistribution && dashboardData.fleetDistribution.length > 0 ? (
              <FleetDistributionChart data={dashboardData.fleetDistribution} />
            ) : (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">{t('dashboard:errors.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard:memberDistribution.title')}
            </Typography>
            {sectionLoading.memberDistribution ? (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : sectionErrors.memberDistribution ? (
              <Alert severity="error">{sectionErrors.memberDistribution}</Alert>
            ) : dashboardData.memberDistribution ? (
              <MemberDistributionStats data={dashboardData.memberDistribution} />
            ) : (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">{t('dashboard:errors.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Relevés kilométriques et Entretien/Carburant */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard:mileageStats.title')}
            </Typography>
            {sectionLoading.mileageStats ? (
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : sectionErrors.mileageStats ? (
              <Alert severity="error">{sectionErrors.mileageStats}</Alert>
            ) : dashboardData.mileageStats ? (
              <MileageStats data={dashboardData.mileageStats} />
            ) : (
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">{t('dashboard:errors.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard:maintenanceAndFuel.title')}
            </Typography>
            {sectionLoading.maintenanceAndFuelStats ? (
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : sectionErrors.maintenanceAndFuelStats ? (
              <Alert severity="error">{sectionErrors.maintenanceAndFuelStats}</Alert>
            ) : dashboardData.maintenanceAndFuelStats ? (
              <MaintenanceAndFuelStats data={dashboardData.maintenanceAndFuelStats} />
            ) : (
              <Box sx={{ height: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">{t('dashboard:errors.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Activité récente et État de la flotte */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard:recentActivity.title')}
            </Typography>
            {sectionLoading.recentActivities ? (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : sectionErrors.recentActivities ? (
              <Alert severity="error">{sectionErrors.recentActivities}</Alert>
            ) : dashboardData.recentActivities ? (
              <RecentActivityList activities={dashboardData.recentActivities} />
            ) : (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">{t('dashboard:errors.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('dashboard:fleetStatus.title')}
            </Typography>
            {sectionLoading.fleetStatus ? (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : sectionErrors.fleetStatus ? (
              <Alert severity="error">{sectionErrors.fleetStatus}</Alert>
            ) : dashboardData.fleetStatus ? (
              <FleetStatusBars data={dashboardData.fleetStatus} />
            ) : (
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="text.secondary">{t('dashboard:errors.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default DashboardPage;